import { useState } from "react";
import type { TextBox } from "store/features/memeGenerator/types";
import { Mode as ModeType } from "../types";
import { Mode } from "../Mode/Mode";
import { ColorList } from "../ColorList/ColorList";
import { Slider } from "components/common/Slider/Slider";
import { MAX_OUTLINE_SIZE, MAX_SHADOW_SIZE } from "store/features/memeGenerator/data/configs";
import { useOutline } from "../../MoreOptions/MoreOptionsContent/Outline/useOutline";
import { roundFloat } from "client/utils/roundFloat";
import { useShadow } from "../../MoreOptions/MoreOptionsContent/Shadow/useShadow";
import { Translate } from "components/common/Translate/Translate";

interface ContentProps {
    textBox: TextBox;
}

export const Content = ({ textBox }: ContentProps) => {
    const [mode, setMode] = useState<ModeType>("text");
    const { handleOutlineChange } = useOutline(textBox);
    const { handleShadowBlurRadiusChange } = useShadow(textBox);
    return (
        <>
            <div
                className="absolute top-full left-0 mt-2 -translate-x-4 translate-y-2 bg-white dark:bg-darkSurface-200 border-[0.5px] border-[#DCDDDF] dark:border-darkSurface-400 rounded-xl text-black dark:text-white"
                style={{
                    boxShadow: `0px 4px 50px rgba(0, 0, 0, 0.08)`,
                }}
            >
                <Mode textBox={textBox} mode={mode} setMode={setMode} />
                <ColorList mode={mode} textBox={textBox} />
                {mode === "outline" && textBox.outline && (
                    <div className="flex flex-col gap-4 pt-2 px-4 pb-4 border-t border-t-[#DCDDDF] dark:border-t-darkSurface-600">
                        <label>
                            <Translate keyName="outline-width" />
                        </label>
                        <div className="flex items-center justify-between border border-irGray-200 dark:border-darkSurface-600 dark:bg-darkSurface-300 p-4 rounded-[10px] gap-4">
                            <div className="w-full">
                                <Slider
                                    defaultValue={textBox.outline.size}
                                    handleChange={handleOutlineChange}
                                    max={MAX_OUTLINE_SIZE}
                                    min={0}
                                />
                            </div>
                            <div className="translate-y-0.5">{textBox.outline.size}px</div>
                        </div>
                    </div>
                )}

                {mode === "shadow" && textBox.shadow && (
                    <div className=" flex flex-col gap-4 pt-2 px-4 pb-4 border-t border-t-[#DCDDDF] dark:border-t-darkSurface-600">
                        <label>
                            <Translate keyName="shadow-width" />
                        </label>
                        <div className="flex items-center justify-between border border-irGray-200 dark:border-darkSurface-600 dark:bg-darkSurface-300 p-4 rounded-[10px] gap-4">
                            <div className="w-full">
                                <Slider
                                    defaultValue={textBox.shadow.blurRadius}
                                    handleChange={handleShadowBlurRadiusChange}
                                    max={MAX_SHADOW_SIZE}
                                    min={0}
                                />
                            </div>
                            <div className="translate-y-0.5">{roundFloat(textBox.shadow.blurRadius)}px</div>
                        </div>
                    </div>
                )}
            </div>
            <div className="absolute translate-y-2 top-full left-2 w-4 h-4 bg-white dark:bg-darkSurface-200 rotate-45 rounded-tl-[4px] border-l-[0.5px] border-t-[0.5px] border-[#DCDDDF] dark:border-darkSurface-600" />
        </>
    );
};
